import React, { memo, useCallback } from 'react';
import { PanelAnnotation, FigureCompositionTheme } from '@/store/figure-composition';

interface AnnotationRendererProps {
  annotation: PanelAnnotation;
  panelWidth: number;
  panelHeight: number;
  theme: FigureCompositionTheme;
  isSelected: boolean;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: (e: React.MouseEvent) => void;
}

export const AnnotationRenderer = memo(function AnnotationRenderer({
  annotation,
  panelWidth,
  panelHeight,
  theme,
  isSelected,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: AnnotationRendererProps) {
  // Convert normalized coordinates (0-1) to pixel coordinates
  const x = annotation.x * panelWidth;
  const y = annotation.y * panelHeight;
  const x2 = annotation.x2 !== undefined ? annotation.x2 * panelWidth : undefined;
  const y2 = annotation.y2 !== undefined ? annotation.y2 * panelHeight : undefined;

  // Get style defaults from theme - calculate font size from fontScale
  const baseAnnotationSize = 10; // Base size for annotations
  const fontScale = theme.fontScale ?? (theme.fontSize ? theme.fontSize / 8 : 1.0);
  const fontSize = annotation.style?.fontSizePt ?? Math.round(baseAnnotationSize * fontScale);
  const color = annotation.style?.color ?? '#000000';
  const fontWeight = annotation.style?.fontWeight ?? 'normal';
  const fontStyle = annotation.style?.fontStyle ?? 'normal';
  const opacity = annotation.style?.opacity ?? 1;
  const strokeWidth = annotation.style?.strokeWidthPx ?? 2;

  // Selection and hover styles - use color from annotation style, only override for selection indicator
  // For arrows/brackets, use the actual color unless selected/hovered (then show blue indicator)
  const strokeColor = (isSelected || isHovered) ? (isSelected ? '#3b82f6' : '#60a5fa') : color;
  const strokeWidthFinal = isSelected ? strokeWidth + 1 : isHovered ? strokeWidth + 0.5 : strokeWidth;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onClick(e as any);
    }
  }, [onClick]);

  const commonProps = {
    onMouseEnter,
    onMouseLeave,
    onClick,
    onKeyDown: handleKeyDown,
    style: {
      cursor: 'grab',
      pointerEvents: 'auto' as const,
    },
  };

  // Map anchor values to SVG attributes
  const getTextAnchor = (xanchor?: 'left' | 'center' | 'right'): string => {
    switch (xanchor) {
      case 'left': return 'start';
      case 'center': return 'middle';
      case 'right': return 'end';
      default: return 'start'; // Default to left
    }
  };

  const getDominantBaseline = (yanchor?: 'top' | 'middle' | 'bottom'): string => {
    switch (yanchor) {
      case 'top': return 'hanging';
      case 'middle': return 'middle';
      case 'bottom': return 'baseline';
      default: return 'hanging'; // Default to top
    }
  };

  switch (annotation.type) {
    case 'text': {
      return (
        <text
          x={x}
          y={y}
          fill={color}
          fontSize={fontSize}
          fontWeight={fontWeight}
          fontStyle={fontStyle}
          opacity={opacity}
          fontFamily={theme.fontFamily}
          textAnchor={getTextAnchor(annotation.xanchor)}
          dominantBaseline={getDominantBaseline(annotation.yanchor)}
          {...commonProps}
          className={isSelected ? 'annotation-selected' : isHovered ? 'annotation-hovered' : ''}
          aria-label={`Text annotation: ${annotation.content || 'Text'}`}
          role="button"
          tabIndex={0}
          data-annotation-id={annotation.id}
          data-annotation-type="text"
          data-x={annotation.x.toFixed(3)}
          data-y={annotation.y.toFixed(3)}
        >
          {annotation.content || 'Text'}
        </text>
      );
    }

    case 'arrow': {
      if (x2 === undefined || y2 === undefined) {
        return null; // Arrow needs both start and end points
      }

      // Calculate arrowhead
      const dx = x2 - x;
      const dy = y2 - y;
      const length = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);

      // Arrowhead size
      const arrowheadLength = Math.min(10, length * 0.15);
      const arrowheadAngle = Math.PI / 6; // 30 degrees

      // Arrowhead points
      const arrowheadX1 = x2 - arrowheadLength * Math.cos(angle - arrowheadAngle);
      const arrowheadY1 = y2 - arrowheadLength * Math.sin(angle - arrowheadAngle);
      const arrowheadX2 = x2 - arrowheadLength * Math.cos(angle + arrowheadAngle);
      const arrowheadY2 = y2 - arrowheadLength * Math.sin(angle + arrowheadAngle);

      return (
        <g
          {...commonProps}
          className={isSelected ? 'annotation-selected' : isHovered ? 'annotation-hovered' : ''}
          aria-label={`Arrow annotation${annotation.content ? `: ${annotation.content}` : ''}`}
          role="button"
          tabIndex={0}
          data-annotation-id={annotation.id}
          data-annotation-type="arrow"
          data-x={annotation.x.toFixed(3)}
          data-y={annotation.y.toFixed(3)}
          style={{ filter: 'none' }}
        >
          {/* Arrow line */}
          <line
            x1={x}
            y1={y}
            x2={x2}
            y2={y2}
            stroke={strokeColor}
            strokeWidth={strokeWidthFinal}
            opacity={opacity}
            strokeLinecap="round"
            style={{ filter: 'none' }}
          />
          {/* Arrowhead */}
          <polygon
            points={`${x2},${y2} ${arrowheadX1},${arrowheadY1} ${arrowheadX2},${arrowheadY2}`}
            fill={strokeColor}
            opacity={opacity}
            style={{ filter: 'none' }}
          />
          {/* Optional label */}
          {annotation.content && (
            <text
              x={(x + x2) / 2}
              y={(y + y2) / 2 - 5}
              fill={color}
              fontSize={fontSize}
              fontWeight={fontWeight}
              fontStyle={fontStyle}
              opacity={opacity}
              fontFamily={theme.fontFamily}
              textAnchor="middle"
              dominantBaseline="baseline"
            >
              {annotation.content}
            </text>
          )}
        </g>
      );
    }

    case 'bracket': {
      if (x2 === undefined || y2 === undefined) {
        return null; // Bracket needs both start and end points
      }

      // Bracket style: horizontal line with vertical ticks at ends
      const bracketHeight = 8; // Height of vertical ticks
      const isHorizontal = Math.abs(y2 - y) < Math.abs(x2 - x);

      if (isHorizontal) {
        // Horizontal bracket
        const leftX = Math.min(x, x2);
        const rightX = Math.max(x, x2);
        const centerY = (y + y2) / 2;

        return (
          <g
            {...commonProps}
            className={isSelected ? 'annotation-selected' : isHovered ? 'annotation-hovered' : ''}
            aria-label={`Bracket annotation${annotation.content ? `: ${annotation.content}` : ''}`}
            role="button"
            tabIndex={0}
            data-annotation-id={annotation.id}
            data-annotation-type="bracket"
            data-x={annotation.x.toFixed(3)}
            data-y={annotation.y.toFixed(3)}
            style={{ filter: 'none' }}
          >
            {/* Horizontal line */}
            <line
              x1={leftX}
              y1={centerY}
              x2={rightX}
              y2={centerY}
              stroke={strokeColor}
              strokeWidth={strokeWidthFinal}
              opacity={opacity}
              style={{ filter: 'none' }}
            />
            {/* Left vertical tick */}
            <line
              x1={leftX}
              y1={centerY - bracketHeight / 2}
              x2={leftX}
              y2={centerY + bracketHeight / 2}
              stroke={strokeColor}
              strokeWidth={strokeWidthFinal}
              opacity={opacity}
              style={{ filter: 'none' }}
            />
            {/* Right vertical tick */}
            <line
              x1={rightX}
              y1={centerY - bracketHeight / 2}
              x2={rightX}
              y2={centerY + bracketHeight / 2}
              stroke={strokeColor}
              strokeWidth={strokeWidthFinal}
              opacity={opacity}
              style={{ filter: 'none' }}
            />
            {/* Label above bracket */}
            {annotation.content && (
              <text
                x={(leftX + rightX) / 2}
                y={centerY - bracketHeight / 2 - 5}
                fill={color}
                fontSize={fontSize}
                fontWeight={fontWeight}
                fontStyle={fontStyle}
                opacity={opacity}
                fontFamily={theme.fontFamily}
                textAnchor="middle"
                dominantBaseline="baseline"
              >
                {annotation.content}
              </text>
            )}
          </g>
        );
      } else {
        // Vertical bracket (less common but supported)
        const topY = Math.min(y, y2);
        const bottomY = Math.max(y, y2);
        const centerX = (x + x2) / 2;

        return (
          <g
            {...commonProps}
            className={isSelected ? 'annotation-selected' : isHovered ? 'annotation-hovered' : ''}
            aria-label={`Bracket annotation${annotation.content ? `: ${annotation.content}` : ''}`}
            role="button"
            tabIndex={0}
            data-annotation-id={annotation.id}
            data-annotation-type="bracket"
            data-x={annotation.x.toFixed(3)}
            data-y={annotation.y.toFixed(3)}
            style={{ filter: 'none' }}
          >
            {/* Vertical line */}
            <line
              x1={centerX}
              y1={topY}
              x2={centerX}
              y2={bottomY}
              stroke={strokeColor}
              strokeWidth={strokeWidthFinal}
              opacity={opacity}
              style={{ filter: 'none' }}
            />
            {/* Top horizontal tick */}
            <line
              x1={centerX - bracketHeight / 2}
              y1={topY}
              x2={centerX + bracketHeight / 2}
              y2={topY}
              stroke={strokeColor}
              strokeWidth={strokeWidthFinal}
              opacity={opacity}
              style={{ filter: 'none' }}
            />
            {/* Bottom horizontal tick */}
            <line
              x1={centerX - bracketHeight / 2}
              y1={bottomY}
              x2={centerX + bracketHeight / 2}
              y2={bottomY}
              stroke={strokeColor}
              strokeWidth={strokeWidthFinal}
              opacity={opacity}
              style={{ filter: 'none' }}
            />
            {/* Label */}
            {annotation.content && (
              <text
                x={centerX + bracketHeight / 2 + 5}
                y={(topY + bottomY) / 2}
                fill={color}
                fontSize={fontSize}
                fontWeight={fontWeight}
                fontStyle={fontStyle}
                opacity={opacity}
                fontFamily={theme.fontFamily}
                textAnchor="start"
                dominantBaseline="middle"
              >
                {annotation.content}
              </text>
            )}
          </g>
        );
      }
    }

    default:
      return null;
  }
});

